// #1975 — read the invitation token + path discriminator off the redirect the
// magic-link round-trip carried into /register, so they can be forwarded to
// POST /auth/register as explicit fields.
//
// DELIBERATELY NOT in invitationFlow.ts: that file is a byte-for-byte mirror of
// apps/api/src/utils/invitationFlow.ts and must stay that way (its own header
// says so). This helper is frontend-only transport with no backend twin.
//
// TRUST BOUNDARY — this parse is NOT one. Everything it produces is
// attacker-controllable (the user can type any ?redirect=), and the backend
// treats it as such: it hashes the token, requires a match against a live
// PENDING unexpired invitation, and requires that invitation's address to
// equal the address being registered, before it suppresses any email. A
// garbage result here costs a wasted lookup and falls back to ordinary
// verification. What this must NOT do is become the thing the skip keys off —
// that decision lives server-side in auth.ts's skip gate.

export type SkipEligibleInvitationType = 'academic' | 'setup';

// Only the two paths whose token model qualifies (256-bit CSPRNG, SHA-256
// hashed at rest, single-use, expiring). '/accept-invite' — the team path — is
// deliberately absent: its token is the invitation row's primary key and is
// served to any tenant member by the project invitation-list endpoints, so it
// cannot stand in for proof of address control. Held until #1969.
const TYPE_BY_PREFIX: ReadonlyArray<readonly [string, SkipEligibleInvitationType]> = [
  ['/academic/accept', 'academic'],
  ['/setup/accept',    'setup'],
] as const;

export interface InvitationSkipParams {
  invitationToken: string;
  invitationType:  SkipEligibleInvitationType;
}

/**
 * Extract `{ invitationToken, invitationType }` from a same-origin redirect
 * such as `/academic/accept?token=<raw>`.
 *
 * Returns null when the redirect is absent, is not one of the two eligible
 * paths, or carries no token — all of which mean "register normally, with
 * verification".
 */
export function readInvitationSkipParams(
  redirect: string | null | undefined,
): InvitationSkipParams | null {
  if (!redirect) return null;

  // Same-origin shape gate BEFORE parsing. `new URL(value, base)` ignores the
  // base whenever `value` is itself absolute, so parsing first would happily
  // accept 'https://evil.test/academic/accept?token=…' — its pathname matches
  // an eligible prefix and it would yield skip params from an off-origin URL.
  //
  // The tab/newline strip below is NOT optional decoration. The WHATWG URL
  // parser DELETES every ASCII tab, LF and CR from its input before parsing,
  // which happens AFTER a leading-character check has already run. So
  // "/\t/evil.test/academic/accept?token=x" passes a naive
  // startsWith('/')/!startsWith('//') gate, then parses as the
  // protocol-relative "//evil.test/academic/accept?token=x" — origin
  // evil.test, pathname /academic/accept, an exact prefix match. Rejecting
  // the three strippable characters outright is what makes the gate hold on
  // the value the parser will actually see rather than the one we inspected.
  if (
    !redirect.startsWith('/') ||
    redirect.startsWith('//') ||
    redirect.startsWith('/\\') ||
    /[\t\n\r]/.test(redirect)
  ) {
    return null;
  }

  // Parse against a dummy base so a path-only string is legal input. Anything
  // malformed throws and is treated as no-skip.
  let parsed: URL;
  try {
    parsed = new URL(redirect, 'http://placeholder.invalid');
  } catch {
    return null;
  }

  const match = TYPE_BY_PREFIX.find(([prefix]) => parsed.pathname === prefix);
  if (!match) return null;

  const token = parsed.searchParams.get('token');
  if (!token) return null;

  return { invitationToken: token, invitationType: match[1] };
}
